import type { SSRAuthClient } from '@react-keycloak/ssr';

type SessionAuthClient = SSRAuthClient & {
  authenticated?: boolean;
  token?: string;
  tokenParsed?: {
    sub?: string;
    exp?: number;
    realm_access?: { roles?: string[] };
    resource_access?: Record<string, { roles?: string[] } | undefined>;
  };
  login?: (options?: { redirectUri?: string }) => Promise<void>;
};

export type SessionAuthLease = {
  subject: string | null;
  generation: number;
  signal: AbortSignal;
  isCurrent(): boolean;
};

let keycloakRef: SessionAuthClient | null = null;
let lastSubject: string | null = null;
let lastRealmRoles: readonly string[] = [];
let sessionIdentityCommitted = false;
let authGeneration = 0;
let sessionBoundaryGeneration = 0;
let authController = new AbortController();
let refreshPromise: Promise<boolean> | null = null;
let refreshClient: SessionAuthClient | null = null;
let proactiveRefreshInterval: ReturnType<typeof setInterval> | null = null;
let proactiveRefreshClient: SessionAuthClient | null = null;
let reauthenticationPromise: Promise<void> | null = null;
const sessionListeners = new Set<() => void>();

const PROACTIVE_REFRESH_POLL_MS = 60 * 1000;
const PROACTIVE_REFRESH_MIN_VALIDITY_SECONDS = 5 * 60;
// The eligibility gate must sit well inside the updateToken margin: if both
// used the same number, a token whose full lifetime is ~5 minutes would be
// eligible on every 60s poll and refresh each time (the refresh becomes
// self-triggering).
const PROACTIVE_REFRESH_TRIGGER_SECONDS = 2 * 60;

const subjectFrom = (instance: SessionAuthClient | null): string | null => (instance?.authenticated ? (instance.tokenParsed?.sub ?? null) : null);
const realmRolesFrom = (instance: SessionAuthClient | null): readonly string[] =>
  instance?.authenticated && Array.isArray(instance.tokenParsed?.realm_access?.roles) ? [...instance.tokenParsed.realm_access.roles].sort() : [];
const sameRoles = (left: readonly string[], right: readonly string[]): boolean =>
  left.length === right.length && left.every((role, index) => role === right[index]);

export const setSessionKeycloak = (instance: SSRAuthClient | null): void => {
  // Session identity mutation is a client-only concern; SSR must stay inert.
  if (typeof window === 'undefined') return;
  const nextInstance = instance as SessionAuthClient | null;
  const nextSubject = subjectFrom(nextInstance);
  const nextRealmRoles = realmRolesFrom(nextInstance);
  const firstCommit = !sessionIdentityCommitted;
  if (!firstCommit && keycloakRef === nextInstance && lastSubject === nextSubject && sameRoles(lastRealmRoles, nextRealmRoles)) return;

  keycloakRef = nextInstance;
  lastSubject = nextSubject;
  lastRealmRoles = nextRealmRoles;
  sessionIdentityCommitted = true;
  refreshPromise = null;
  refreshClient = null;
  reauthenticationPromise = null;
  authGeneration += 1;
  if (!firstCommit) sessionBoundaryGeneration += 1;
  const previousController = authController;
  authController = new AbortController();
  previousController.abort();
  for (const listener of sessionListeners) listener();
};

export const getAccessToken = (): string | null => keycloakRef?.token ?? null;
export const getSessionKeycloak = (): SSRAuthClient | null => keycloakRef;
export const getSessionSubject = (): string | null => lastSubject;
export const getSessionRealmRoles = (): readonly string[] => lastRealmRoles;
export const getSessionOrganizationRoles = (organization: string): readonly string[] => {
  const roles = keycloakRef?.authenticated ? keycloakRef.tokenParsed?.resource_access?.[`org_${organization}`]?.roles : undefined;
  return Array.isArray(roles) ? roles.filter((role): role is string => typeof role === 'string') : [];
};
export const hasCommittedSessionIdentity = (): boolean => sessionIdentityCommitted;
export const getAuthGeneration = (): number => authGeneration;
export const getSessionBoundaryGeneration = (): number => sessionBoundaryGeneration;
export const getSessionAuthSignal = (): AbortSignal => authController.signal;
export const acquireSessionAuthLease = (): SessionAuthLease => {
  const subject = lastSubject;
  const generation = authGeneration;
  const signal = authController.signal;

  return {
    subject,
    generation,
    signal,
    isCurrent: () => !signal.aborted && generation === authGeneration && subject === lastSubject,
  };
};
export const subscribeSessionIdentity = (listener: () => void): (() => void) => {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
};

// Test-only reset used by the shared unit-test teardown: a test file that
// forgets cleanup() leaks its subscriber for the worker's lifetime, and every
// later setSessionKeycloak call (the teardown itself makes one) then fires a
// React update on an unmounted tree.
export const __resetSessionListenersForTests = (): void => {
  sessionListeners.clear();
};

export const __resetSessionIdentityForTests = (): void => {
  stopProactiveTokenRefresh();
  keycloakRef = null;
  lastSubject = null;
  lastRealmRoles = [];
  sessionIdentityCommitted = false;
  authGeneration = 0;
  sessionBoundaryGeneration = 0;
  refreshPromise = null;
  refreshClient = null;
  reauthenticationPromise = null;
  const previousController = authController;
  authController = new AbortController();
  previousController.abort();
};

export const requestReauthentication = (): Promise<void> => {
  if (reauthenticationPromise) return reauthenticationPromise;
  const client = keycloakRef;
  const subject = lastSubject;
  const generation = authGeneration;
  const login = client?.login;
  if (!client?.authenticated || !login || typeof window === 'undefined') return Promise.resolve();
  const redirectUri = window.location.href;

  const pending = Promise.resolve()
    .then(async () => {
      if (client !== keycloakRef || subject !== lastSubject || generation !== authGeneration || !client.authenticated) return;
      await login.call(client, { redirectUri });
    })
    .catch((error: unknown) => {
      if (reauthenticationPromise === pending) reauthenticationPromise = null;
      throw error;
    });
  reauthenticationPromise = pending;
  return pending;
};

export const refreshAccessToken = (): Promise<boolean> => {
  const client = keycloakRef;
  if (!client) return Promise.resolve(false);
  if (refreshPromise && refreshClient === client) return refreshPromise;

  refreshClient = client;
  const promise: Promise<boolean> = client
    .updateToken(-1)
    .then(() => Boolean(client.token))
    .catch((error: unknown) => {
      // Callers only see the boolean; without this the failure reason (expired
      // refresh token, Keycloak outage, network) is silently discarded.
      console.error('Session token refresh failed', error);
      return false;
    })
    .finally(() => {
      // Promise identity, not client identity: setSessionKeycloak clears the
      // latch on every identity transition, so the same instance can be
      // re-registered and start a second refresh while the first is settling.
      // Comparing clients would let the older refresh clear the newer latch.
      if (refreshPromise === promise) {
        refreshPromise = null;
        refreshClient = null;
      }
    });
  refreshPromise = promise;

  return promise;
};

export const stopProactiveTokenRefresh = (): void => {
  if (proactiveRefreshInterval) {
    clearInterval(proactiveRefreshInterval);
    proactiveRefreshInterval = null;
  }
  proactiveRefreshClient = null;
};

export const startProactiveTokenRefresh = (instance: SSRAuthClient): void => {
  // A server-side interval would outlive the request; client-only.
  if (typeof window === 'undefined') return;
  const client = instance as SessionAuthClient;
  if (proactiveRefreshClient === client && proactiveRefreshInterval) return;
  stopProactiveTokenRefresh();
  proactiveRefreshClient = client;

  const refreshIfNeeded = async (): Promise<void> => {
    if (keycloakRef !== client) return;
    const expiresAt = client.tokenParsed?.exp;
    if (!expiresAt || expiresAt * 1000 - Date.now() > PROACTIVE_REFRESH_TRIGGER_SECONDS * 1000) return;

    try {
      await client.updateToken(PROACTIVE_REFRESH_MIN_VALIDITY_SECONDS);
    } catch (error) {
      console.error('Proactive token refresh failed:', error);
    }
  };

  void refreshIfNeeded();
  proactiveRefreshInterval = setInterval(() => {
    void refreshIfNeeded();
  }, PROACTIVE_REFRESH_POLL_MS);
};
